import {Injectable}                                     from '@angular/core';
import {map}                                            from 'rxjs/operators';
import {Guid, ObjectFactory}                            from 'ts-tooling';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                       from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {nameOf}                                         from '../../../ap-core/utils/ap-name-of';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                       from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {MapViewMode}                                    from '../../../ap-interface';
import {
  ApDynGridGroupColumnConfig
}                                                       from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  ApDynGridEditColumnConfig
}                                                       from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {MapViewStore}                                   from '../../../stores/layout/mapview.store';
import {
  GetRoundNumericService
}                                                       from '../../../ap-utils/service/get-round-numeric.service';
import {ApTimeSpanUtil, GetRoundNumericPipe}            from '../../../ap-utils';
import {DriverStore}                                    from '../../../stores/docu/driver.store';
import {MachineStore}                                   from '../../../stores/docu/machine.store';
import {InstrumentStore}                                from '../../../stores/docu/instrument.store';
import {UnitsStore}                                     from '../../../stores/common/units.store';
import {ApDateService}                                  from '../../../ap-core/services/ap-date-service';
import {ApTranslationService}                           from '../../../ap-utils/service/ap-translation.service';
import {FieldStore}                                     from '../../../stores/farm/field.store';
import {TimeAreaService}                                from '../../../services/data/time-area.service';
import {FormatType}                                     from '../../../ap-utils/types/ap-format-duration.types';
import {TimeFormatPipe}                                 from '../../../ap-utils/pipes/ap-format-duration.pipe';
import {FwEntryComponentsService}                       from '../services/fw-entry-components.service';
import {IFwPlanBookingEditData, IFwPlanBookingGridItem} from './fw-plan-booking.types';
import {
  ApTooltipContentService
}                                                       from '../../../ap-utils/service/ap-tooltip-content.service';
import IGuid = System.IGuid;
import ILanguage = Data.Language.ILanguage;
import ISettings = Data.BaseData.ISettings;
import IFieldWorkPlanBook = Data.TaskManagement.IFieldWorkPlanBook;

@Injectable()
export class FwPlanBookingGridConfig {
  constructor(private unitStore: UnitsStore,
              private fieldStore: FieldStore,
              private driverStore: DriverStore,
              private mapViewStore: MapViewStore,
              private machineStore: MachineStore,
              private instrumentStore: InstrumentStore,
              private dateService: ApDateService,
              private timeAreaService: TimeAreaService,
              private translationService: ApTranslationService,
              private tooltipContentService: ApTooltipContentService,
              private roundNumericService: GetRoundNumericService,
              private fwEntryComponentService: FwEntryComponentsService,
              private timeFormatPipe: TimeFormatPipe,
              private roundNumericPipe: GetRoundNumericPipe) {
  }

  /**
   * Gets grid items from plan books based on provided parameters
   * @param planBooks Array of field work plan books
   * @param plansToShow Array of plan IDs to show
   * @param isBookingProcess Flag indicating if this is booking process
   * @returns Array of grid items for display
   * @public
   */
  public getGridItems(planBooks: IFieldWorkPlanBook[], plansToShow: IGuid[], isBookingProcess: boolean): IFwPlanBookingGridItem[] {
    return planBooks.filter(x => !!x && plansToShow.includes(x.Id))
      .filter(x => isBookingProcess ? !x.BookedAt : !!x.BookedAt)
      .map(planBook => {
        const driver = this.driverStore.getDriverById(planBook.ExportDriver);
        const instrument = this.instrumentStore.getInstrumentById(planBook.ExportInstrument);
        const machine = this.machineStore.getMachineById(planBook.ExportMachine);
        const field = this.fieldStore.getFieldById(planBook.FieldId);
        const fieldGeom = this.fieldStore.getCurrentFieldGeom(field);
        const start = this.fwEntryComponentService.getTimeSpanInDate(planBook.Start);
        const haPerHour = this.timeAreaService.getHaPerHoursFromTotalMilliseconds(fieldGeom?.AreaHa,
          planBook.Duration?.TotalMilliseconds);
        const gridItem: IFwPlanBookingGridItem = {
          Id: planBook.Id,
          FieldId: field?.Id,
          FieldName: field?.FieldName,
          FieldNumber: field?.FieldNumber,
          FieldSecondNumber: field?.FieldSubnumber,
          FieldCombinedNumber: this.fieldStore.getFieldNumberAndSubNumber(field),
          ApplicationDate: this.dateService.getDateMidnight(planBook.ApplicationDate),
          AreaHa: fieldGeom?.AreaHa ?? 0,
          Start: start,
          HaPerHour: haPerHour,
          DurationInMilliseconds: planBook.Duration?.TotalMilliseconds,
          Value: planBook.Value,
          Unit: this.translationService.translate(planBook.Unit?.Key),
          WorkType: this.translationService.translate(planBook.WorkType?.Description),
          DriverId: driver?.Id,
          DriverName: driver?.FirstNameFullName,
          MachineId: machine?.Id,
          MachineName: machine?.NameWithSign,
          InstrumentId: instrument?.Id,
          InstrumentName: instrument?.Name,
          PlanBookCopy: ObjectFactory.Copy(planBook)
        };
        return gridItem;
      });
  }

  /**
   * Updates an existing grid item with edited data
   * @param gridItem Grid item to update
   * @param editData New data to apply
   * @param nullableCheckEnabled Whether to check for null values
   * @public
   */
  public updateGridItems(gridItem: IFwPlanBookingGridItem,
                         editData: IFwPlanBookingEditData, nullableCheckEnabled: boolean): void {
    if (!gridItem || !editData) {
      return;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.ApplicationDate)) {
      const applicationDate = this.dateService.getDateNoon(editData.ApplicationDate);
      gridItem.ApplicationDate = applicationDate;
      gridItem.PlanBookCopy.ApplicationDate = applicationDate;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.Start)) {
      gridItem.Start = editData.Start;
      gridItem.PlanBookCopy.Start = ApTimeSpanUtil.getTimeSpanFromDate(editData.Start, true);
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.HaPerHour)) {
      const duration = this.timeAreaService.getHoursPerHaFromTotalMilliseconds(gridItem.AreaHa,
        editData.HaPerHour);
      gridItem.HaPerHour = editData.HaPerHour;
      gridItem.DurationInMilliseconds = duration;
      gridItem.PlanBookCopy.Duration = ApTimeSpanUtil.getTimeSpanFromMilliseconds(duration, true);
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && Number.isFinite(editData.Value))) {
      gridItem.Value = editData.Value;
      gridItem.PlanBookCopy.Value = editData.Value;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && !!editData.Unit)) {
      const unit = this.unitStore.getUnitWithUnit(editData.Unit);
      gridItem.Unit = this.translationService.translate(unit?.Key);
      gridItem.PlanBookCopy.Unit = unit;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && this.isGuidValid(editData.DriverId?.toString()))) {
      const driver = this.driverStore.getDriverById(editData.DriverId);
      gridItem.DriverId = driver?.Id;
      gridItem.DriverName = driver?.FirstNameFullName;
      gridItem.PlanBookCopy.ExportDriver = driver?.Id;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && this.isGuidValid(editData.InstrumentId?.toString()))) {
      const instrument = this.instrumentStore.getInstrumentById(editData.InstrumentId);
      gridItem.InstrumentId = instrument?.Id;
      gridItem.InstrumentName = instrument?.Name;
      gridItem.PlanBookCopy.ExportInstrument = instrument?.Id;
    }
    if (!nullableCheckEnabled || (nullableCheckEnabled && this.isGuidValid(editData.MachineId?.toString()))) {
      const machine = this.machineStore.getMachineById(editData.MachineId);
      gridItem.MachineId = machine?.Id;
      gridItem.MachineName = machine?.NameWithSign;
      gridItem.PlanBookCopy.ExportMachine = machine?.Id;
    }
  }

  /**
   * Gets column configuration for grid display
   * @param language Language settings for localization
   * @param setting Application settings
   * @returns Array of column configurations
   * @public
   */
  public getGridColumns(language: ILanguage, setting: ISettings): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('FieldNumber'),
        title: 'Docu_Ini__Number',
        width: 60,
        cssClass: 'right',
        hide: true,
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('FieldSecondNumber'),
        title: 'Docu_Ini_Subnumber',
        width: 60,
        cssClass: 'right',
        hide: true,
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('FieldCombinedNumber'),
        title: 'Docu_Ini__Number',
        width: 70,
        hide: new ApDynGridColumnHideConfig({
          selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((mode) => mode === MapViewMode.HIDE))
        }),
        filterable: true,
        headerFilterable: true,
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('FieldName'),
        title: 'Docu_Ini__Name',
        width: 180,
        sortIndex: 0,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('AreaHa'),
        title: 'Docu_Ini__Area',
        width: 60,
        cssClass: 'right',
        tooltip: (dataItem: IFwPlanBookingGridItem) => this.roundNumericService.stringify(dataItem?.AreaHa, 4),
        filterable: true,
        filterType: FilterType.NUMBER,
        headerFilterable: true,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [setting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('ApplicationDate'),
        title: 'Base__SortItemDate',
        format: language.DateFormat,
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('Value'),
        title: 'Global_Value',
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('Unit'),
        title: 'Settings__Lbl_Menu_Unit',
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('Start'),
        title: 'Docu_Ini__StartTime',
        format: 'HH:mm',
        hide: true,
        width: 70,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: nameOf<IFwPlanBookingGridItem>('DurationInMilliseconds'),
        title: `${this.translationService.translate('TotalTime')} [${this.translationService.translate('Base__UnitH')}]`,
        hide: true,
        width: 70,
        cssClass: 'right',
        headerFilterable: true,
        tooltip: (dataItem: IFwPlanBookingGridItem) => this.tooltipContentService.getCapacityPerformanceTooltip(dataItem.HaPerHour),
        pipes: [{
          pipe: this.timeFormatPipe,
          args: [{
            formatType: FormatType.Decimals,
            suffixValue: 'Base__UnitH',
            decimalsFormatOptions: {
              decimalsCount: 1,
              decimalsSeparator: language.DecimalSeperator
            }
          }]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Settings__Lbl_Menu_Resources',
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IFwPlanBookingGridItem>('MachineName'),
            title: 'Base__Engine',
            width: 100,
            hide: true,
            headerFilterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IFwPlanBookingGridItem>('InstrumentName'),
            title: 'AgriConnect_Ini__Tool',
            width: 100,
            hide: true,
            headerFilterable: true
          }),
          new ApDynGridPropertyColumnConfig({
            field: nameOf<IFwPlanBookingGridItem>('DriverName'),
            title: 'Docu_Ini__Motorist',
            width: 100,
            hide: true,
            headerFilterable: true
          })
        ]
      }),
      new ApDynGridEditColumnConfig({})
    ];
  }

  private isGuidValid(value: string): boolean {
    return !!value && Guid.Validate(value);
  }
}
